import { Icon } from '@/components';
import { useIsProjectDemo } from '@/features/projectDemo';
import { useRemoveSpanFromListMutation } from '@/features/customListSlice';
import type { CustomListBasics } from '@/features/types';
import { Menu, Button, useMantineTheme } from '@mantine/core';
import { type FC, useCallback, useEffect, useState } from 'react';
import { ChevronDown, Copy, Minus, Plus, Upload } from 'react-feather';
import { notifications } from '@mantine/notifications';
import { useCustomListSelectorModal } from '@/views/list/custom-lists/useCustomListSelectorModal';
import { useClickOutside, useClipboard, useDisclosure } from '@mantine/hooks';
import { useClientTerminology } from '@/views/list/NetworkTerms';
import { CustomListBadge } from '@/views/list/custom-lists/CustomListBadge';
import classes from './SpanSupportActions.module.css';
import TagManager from 'react-gtm-module';
import { GoogleEvents } from '@/features/googleEvents';

export const SpanSupportActions: FC<{
  lists: CustomListBasics[];
  level2: string;
  level3: string;
  /** Span display name for copying */
  spanName?: string;
  /** Span URL for copying */
  spanUrl?: string;
}> = (props) => {
  const [currentLists, setCurrentLists] = useState<CustomListBasics[]>(
    props.lists || []
  );
  const [deleteFromList, { isLoading: isRemoving }] =
    useRemoveSpanFromListMutation();
  const { modalComponent, openModal: openListSelectorModal } =
    useCustomListSelectorModal({
      mode: 'individual',
      level2: props.level2,
      level3: props.level3,
    });
  const [opened, { close, toggle }] = useDisclosure(false);
  const isProjectDemo = useIsProjectDemo();
  const intl = useClientTerminology();
  const theme = useMantineTheme();
  const [targetNode, setTargetNode] = useState<HTMLElement | null>(null);
  useClickOutside(
    () => {
      close();
    },
    null,
    [targetNode]
  );

  // This is needed to ensure the state is updated when the lists prop changes
  // eg. when a span is added to a list
  useEffect(() => {
    setCurrentLists(props.lists || []);
  }, [props.lists]);

  const deleteSpanFromList = useCallback(
    async (list: CustomListBasics, level2: string, level3: string) => {
      try {
        await deleteFromList({
          listId: list.listId,
          level2,
          level3,
        }).unwrap();

        setCurrentLists((prev) => prev.filter((l) => l.listId !== list.listId));
      } catch (err) {
        const errorMessage = intl.formatMessage({
          id: 'customList.removeFromListError',
        });
        notifications.show({
          title: intl.formatMessage({ id: 'action.error' }),
          message: `${errorMessage}: ${err}`,
          color: 'red',
        });

        // putting the list back in the state, because the delete failed
        setCurrentLists((prev) => {
          return [
            ...prev,
            {
              listId: list.listId,
              name: list.name,
            },
          ];
        });
      }
    },
    [deleteFromList, intl]
  );

  const moreButton = (
    <Button
      ref={setTargetNode}
      variant="default"
      size="sm"
      radius="lg"
      onClick={toggle}
      rightSection={
        <Icon
          Component={ChevronDown}
          size={16}
          color={theme.other.mainColor.accent.normal}
        />
      }
      className={classes.moreButton}
      data-testid="span-support-more-button"
    >
      {intl.formatMessage({
        id: 'spanMapPopover.more',
        defaultMessage: 'More',
      })}
    </Button>
  );

  const currentListsOptions = currentLists.map((list) => (
    <div key={list.listId}>
      <Button
        leftSection={
          <Icon
            Component={Minus}
            size={16}
            color={theme.other.mainColor.accent.normal}
          />
        }
        variant="subtle"
        fullWidth
        loading={isRemoving}
        style={{ display: 'flex', justifyContent: 'flex-start' }}
        data-testid={`span-support-remove-from-${list.listId}`}
        onClick={() => {
          TagManager.dataLayer({
            dataLayer: {
              event: GoogleEvents.projects.removing.span,
            },
          });

          deleteSpanFromList(list, props.level2, props.level3);
        }}
      >
        {intl.formatMessage({
          id: 'customList.removeFrom',
          defaultMessage: 'Remove from',
        })}
        <CustomListBadge name={list.name} className={classes.badge} />
      </Button>
    </div>
  ));

  const clipboard = useClipboard({ timeout: 1000 });

  const copyNameButton = props.spanName ? (
    <div key="copy-name">
      <Button
        leftSection={
          <Icon
            Component={Copy}
            size={16}
            color={theme.other.mainColor.accent.normal}
          />
        }
        variant="subtle"
        fullWidth
        onClick={() => {
          clipboard.copy(props.spanName);
          TagManager.dataLayer({
            dataLayer: { event: GoogleEvents.copy_span_name },
          });
          close();
        }}
        style={{ display: 'flex', justifyContent: 'flex-start' }}
      >
        {clipboard.copied ? 'Copied!' : 'Copy span name'}
      </Button>
    </div>
  ) : null;

  const copyUrlButton = props.spanUrl ? (
    <div key="copy-url">
      <Button
        leftSection={
          <Icon
            Component={Upload}
            size={16}
            color={theme.other.mainColor.accent.normal}
          />
        }
        variant="subtle"
        fullWidth
        onClick={() => {
          clipboard.copy(props.spanUrl);
          TagManager.dataLayer({
            dataLayer: { event: GoogleEvents.copy_span_url },
          });
          close();
        }}
        style={{ display: 'flex', justifyContent: 'flex-start' }}
      >
        {clipboard.copied ? 'Copied!' : 'Copy span URL'}
      </Button>
    </div>
  ) : null;

  const addToListButton = (
    <div key="add-to-list">
      <Button
        leftSection={
          <Icon
            Component={Plus}
            size={16}
            color={theme.other.mainColor.accent.normal}
          />
        }
        variant="subtle"
        fullWidth
        onClick={() => {
          openListSelectorModal();
          close();
        }}
        style={{ display: 'flex', justifyContent: 'flex-start' }}
      >
        {intl.formatMessage({
          id: 'customList.addToList',
          defaultMessage: 'Add to list',
        })}
      </Button>
    </div>
  );

  return (
    <>
      {modalComponent}
      <Menu opened={opened} offset={{ mainAxis: 10, crossAxis: 100 }}>
        <Menu.Target>{moreButton}</Menu.Target>
        <Menu.Dropdown key={props.level2} className={classes.popoverDropdown}>
          {copyNameButton}
          {copyUrlButton}
          {!isProjectDemo && currentListsOptions}
          {!isProjectDemo && addToListButton}
        </Menu.Dropdown>
      </Menu>
    </>
  );
};
